import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { formatEur, formatNumber } from '@/lib/format';
import { getTierColor } from '@/lib/tierColors';
import { useSimulatorState } from '@/store/useSimulatorState';

import { Card } from '@/components/ui/card';

const MODEL_LABELS: Record<string, string> = {
  's-curve': 'Courbe en S',
  'linear-ramp': 'Rampe linéaire',
  constant: 'Constant',
};

function Row({
  label,
  detail,
  onClick,
  expandable,
  expanded,
}: {
  label: string;
  detail: string;
  onClick: () => void;
  expandable?: boolean;
  expanded?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-[13px] border px-4 py-3.5 text-left text-sm transition-colors hover:border-primary/40"
    >
      <span>
        <span className="block font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{detail}</span>
      </span>
      {expandable ? (
        <ChevronDown
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      ) : (
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      )}
    </button>
  );
}

/**
 * Aperçu cliquable de la configuration (Équipe, Paliers, Acquisition,
 * Projection), inspiré des lignes-résumé de la maquette. Ouvre le
 * `ConfigDrawer` existant sur l'onglet correspondant — pas de système
 * d'édition inline séparé.
 */
export function ConfigQuickLinks({ onOpenTab }: { onOpenTab: (tab: string) => void }) {
  const team = useSimulatorState((s) => s.team);
  const recurringCosts = useSimulatorState((s) => s.recurringCosts);
  const tiers = useSimulatorState((s) => s.tiers);
  const acquisition = useSimulatorState((s) => s.acquisition);
  const horizonMonths = useSimulatorState((s) => s.projection.horizonMonths);
  const [tiersExpanded, setTiersExpanded] = useState(false);

  const chargesMultiplier = team.chargesMultiplier ?? 1;
  const monthlyCost =
    (team.headcount * team.avgAnnualCostPerFTE * chargesMultiplier) / 12 +
    recurringCosts.reduce((sum, c) => sum + c.monthlyAmount, 0);

  const shareTotal = tiers.reduce((sum, t) => sum + t.acquisitionShare, 0);

  return (
    <div className="flex flex-col gap-2.5">
      <Row
        label="Équipe & coûts"
        detail={`${formatEur(monthlyCost)}/mois · ${formatNumber(team.headcount)} ETP`}
        onClick={() => onOpenTab('team')}
      />

      <Card className="gap-0 overflow-hidden rounded-[13px] border p-0">
        <Row
          label="Paliers"
          detail={`${tiers.length} palier(s) · ${formatNumber(shareTotal * 100, 0)} % réparti`}
          onClick={() => setTiersExpanded((v) => !v)}
          expandable
          expanded={tiersExpanded}
        />
        {tiersExpanded && (
          <div className="flex flex-col gap-2 border-t bg-accent/40 px-4 py-3">
            {tiers.map((tier, i) => (
              <button
                key={tier.id}
                type="button"
                onClick={() => onOpenTab('tiers')}
                className="flex items-center justify-between text-xs"
              >
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="inline-block size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: getTierColor(i) }}
                  />
                  {tier.name}
                </span>
                <span className="font-medium">
                  {formatEur(tier.monthlyPrice)} · {formatNumber(tier.monthlyChurnRate * 100, 0)} %
                  churn
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Row
        label="Acquisition"
        detail={`${MODEL_LABELS[acquisition.model ?? 'constant']} · ${formatNumber(
          acquisition.newClientsPerMonth,
        )} clients/mois`}
        onClick={() => onOpenTab('acquisition')}
      />

      <Row
        label="Projection"
        detail={`Horizon ${horizonMonths} mois`}
        onClick={() => onOpenTab('projection')}
      />
    </div>
  );
}
